import { MenuItem, SelectChangeEvent, Typography } from '@mui/material';
import { AdvancedDatePicker } from '@sb/cc-design-system';
import { CreateCcDataResult } from 'gcf-player/shared-utils';
import { isEmpty } from 'lodash';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import DesignSystemThemeProvider from 'src/components/designSystem/DesignSystemThemeProvider';
import { EmptyProvider } from 'src/components/emptyProvider';
import { GcfPreview } from 'src/components/gcfPreview';
import { LoadingProvider } from 'src/components/loadingProvider';
import { ScaleContainer } from 'src/components/scaleContainer/ScaleContainer';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import {
  DEFAULT_SCREEN_ALL,
  ISO_DATE_FORMAT,
  SIDE_NAV_WIDTH,
} from 'src/constants';
import { useParamQuery } from 'src/hooks';
import { useSharedUtilsFromGcfPlayer } from 'src/hooks/useSharedUtilsFromGcfPlayer';
import {
  IPreviewOption,
  ISchedulePreviewWithChangeSetContext,
  LocationChannels,
} from 'src/interfaces/SchedulePreview';
import { lang, useUserRole } from 'src/utils';

import { useSchedulePreviewPage } from './hooks/SchedulePreviewPage.hook';
import {
  getPreviewCacheKey,
  getPreviewDataFromCache,
  getPreviewLayerEntries,
  getUniqueSortedPositions,
  updatePreviewLayerCache,
} from './SchedulePreviewPage.helper';
import {
  ICachedPreviewLayerProps,
  IPreviewLayerCache,
} from './SchedulePreviewPage.interface';
import {
  ButtonGroupWrapper,
  GCFPreviewBox,
  PreviewContentBox,
  PreviewContentWrapper,
  PreviewFilters,
  PreviewHeader,
  PreviewHeaderContainer,
  PreviewLayer,
  PreviewWrapper,
  RoundedDropdown,
  StyledStack,
  StyledToggleButton,
} from './SchedulePreviewPage.styled';

const usePreviewLayerCacheRef = () =>
  useRef<IPreviewLayerCache>({ previews: {} });

const useSyncPreviewLayerCache = (
  cacheRef: { current: IPreviewLayerCache },
  previewSource: object | undefined,
  previewCacheKey: string,
  previewData?: CreateCcDataResult,
) => {
  useEffect(() => {
    updatePreviewLayerCache(
      cacheRef,
      previewSource,
      previewCacheKey,
      previewData,
    );
  }, [cacheRef, previewCacheKey, previewData, previewSource]);
};

const CachedPreviewLayer = memo(
  ({
    activePosition,
    activeScreen,
    isVisible,
    previewData,
  }: ICachedPreviewLayerProps) => (
    <PreviewLayer $visible={isVisible}>
      <GcfPreview
        previewData={previewData}
        activePosition={activePosition}
        activeScreen={activeScreen}
        highlightAll={true}
      />
    </PreviewLayer>
  ),
  (prevProps, nextProps) => {
    if (
      prevProps.previewData !== nextProps.previewData ||
      prevProps.isVisible !== nextProps.isVisible
    ) {
      return false;
    }

    if (!nextProps.isVisible) {
      return true;
    }

    return (
      prevProps.activePosition === nextProps.activePosition &&
      prevProps.activeScreen === nextProps.activeScreen
    );
  },
);

export const SchedulePreviewPage = () => {
  const {
    locationGroup,
    selectedDate,
    minDate,
    maxDate,
    selectedScreen,
    selectedPosition,
    selectedDaypart,
    previewOption,
    schedulePreviewData,
    schedulePreviewContext,
    isSchedulePreviewContextLoading,
    daypartOptions,
    isLoadingDayparts,
    selectedDaypartData,
    handleChangeSelectedDate,
    handleChangeSelectedScreen,
    handleChangeSelectedPosition,
    handleChangeSelectedDaypart,
    setPreviewOption,
  } = useSchedulePreviewPage();

  const {
    utils,
    loading: isSharedUtilsLoading,
    error,
  } = useSharedUtilsFromGcfPlayer();

  const { getParamsValue } = useParamQuery();
  const changeSetId = getParamsValue('changeSetID').join();
  const schedulePreviewWithChangeSet = schedulePreviewContext as
    | ISchedulePreviewWithChangeSetContext
    | undefined;
  const beforePreviewLayersRef = usePreviewLayerCacheRef();
  const afterPreviewLayersRef = usePreviewLayerCacheRef();
  const hasChangeSetPreview = !!changeSetId;
  const isAfterPreviewActive = previewOption === IPreviewOption.AFTER;
  const activePreviewCacheKey = useMemo(
    () => getPreviewCacheKey(selectedDaypartData?.name, selectedDate),
    [selectedDate, selectedDaypartData],
  );

  const createPreviewData = useCallback(
    (dataSource?: typeof schedulePreviewData) => {
      if (
        isSchedulePreviewContextLoading ||
        isEmpty(dataSource) ||
        !selectedDaypartData ||
        !utils
      ) {
        return undefined;
      }

      const {
        contents,
        location,
        products,
        config,
        assets,
        components,
        overrides,
        restaurantBridgeData,
      } = dataSource;

      // Group-level preview does not support pricing files, so we force currencySymbolSource
      // to 'project-config' to ensure correct currency symbol resolution during preview.
      const configWithOverrides = {
        ...config,
        projectConfig: {
          ...config?.projectConfig,
          currencySymbolSource: 'project-config',
        },
      };

      return utils.createCcData(
        contents,
        location,
        undefined,
        products,
        configWithOverrides,
        assets,
        components,
        overrides,
        restaurantBridgeData,
        undefined,
        {
          daypartName: selectedDaypartData.name,
          datetime: selectedDate.format(ISO_DATE_FORMAT),
        },
      );
    },
    [isSchedulePreviewContextLoading, selectedDaypartData, selectedDate, utils],
  );
  const schedulePreviewBeforeSource =
    schedulePreviewWithChangeSet?.before ?? schedulePreviewData;
  const schedulePreviewAfterSource =
    schedulePreviewWithChangeSet?.after ?? schedulePreviewData;
  const previewDataBefore: CreateCcDataResult | undefined =
    getPreviewDataFromCache(
      beforePreviewLayersRef,
      schedulePreviewBeforeSource,
      activePreviewCacheKey,
    ) ??
    (!hasChangeSetPreview || !isAfterPreviewActive
      ? createPreviewData(schedulePreviewBeforeSource)
      : undefined);

  const previewDataAfter: CreateCcDataResult | undefined = hasChangeSetPreview
    ? getPreviewDataFromCache(
        afterPreviewLayersRef,
        schedulePreviewAfterSource,
        activePreviewCacheKey,
      ) ??
      (isAfterPreviewActive
        ? createPreviewData(schedulePreviewAfterSource)
        : undefined)
    : undefined;

  const previewData: CreateCcDataResult | undefined = useMemo(() => {
    if (isSchedulePreviewContextLoading || !selectedDaypartData) {
      return undefined;
    }

    return previewOption === IPreviewOption.AFTER
      ? previewDataAfter
      : previewDataBefore;
  }, [
    isSchedulePreviewContextLoading,
    selectedDaypartData,
    previewDataAfter,
    previewDataBefore,
    previewOption,
  ]);

  const cachedBeforePreviews = getPreviewLayerEntries(
    beforePreviewLayersRef,
    schedulePreviewBeforeSource,
    activePreviewCacheKey,
    previewDataBefore,
  );
  const cachedAfterPreviews = getPreviewLayerEntries(
    afterPreviewLayersRef,
    schedulePreviewAfterSource,
    activePreviewCacheKey,
    previewDataAfter,
  );
  const hasActivePreviewAssembly = !!previewData?.activeAssembly;
  const isBeforePreviewVisible = previewOption !== IPreviewOption.AFTER;
  const isAfterPreviewVisible = previewOption === IPreviewOption.AFTER;
  const { isViewOnly } = useUserRole();
  const totalScreens = Object.keys(
    previewData?.activeAssembly?.screens ?? {},
  ).length;

  const uniqueSortedPositions = getUniqueSortedPositions(
    selectedDate,
    previewData?.ccData?.location?.channels as LocationChannels,
    selectedDaypartData?.name,
  );

  const isSelectedPositionInvalid = !uniqueSortedPositions.includes(
    Number(selectedPosition),
  );

  const isSelectedScreenInvalid =
    selectedScreen !== DEFAULT_SCREEN_ALL &&
    Number(selectedScreen) > totalScreens;

  useSyncPreviewLayerCache(
    beforePreviewLayersRef,
    schedulePreviewBeforeSource,
    activePreviewCacheKey,
    previewDataBefore,
  );
  useSyncPreviewLayerCache(
    afterPreviewLayersRef,
    schedulePreviewAfterSource,
    activePreviewCacheKey,
    previewDataAfter,
  );

  useEffect(() => {
    if (!previewData) {
      return;
    }

    if (isSelectedPositionInvalid) {
      handleChangeSelectedPosition('1');
    }
  }, [isSelectedPositionInvalid, previewData, handleChangeSelectedPosition]);

  const renderCachedPreviewLayers = (
    prefix: 'before' | 'after',
    previewEntries: Array<[string, CreateCcDataResult]>,
    isActivePreviewVisible: boolean,
  ) =>
    previewEntries.map(([previewCacheKey, cachedPreviewData]) => (
      <CachedPreviewLayer
        key={`${prefix}-${previewCacheKey}`}
        previewData={cachedPreviewData}
        activePosition={selectedPosition}
        activeScreen={selectedScreen}
        isVisible={
          isActivePreviewVisible &&
          hasActivePreviewAssembly &&
          previewCacheKey === activePreviewCacheKey
        }
      />
    ));

  return (
    <PreviewWrapper>
      <PreviewHeader>
        <PreviewHeaderContainer>
          <CustomTooltip
            width={500}
            placement="bottom"
            overflowLineNumber={1}
            title={`${locationGroup?.name ?? ''} preview`}>
            <Typography variant="h1">
              {`${locationGroup?.name ?? ''} preview`}
            </Typography>
          </CustomTooltip>
          {isViewOnly && <ViewOnlyAlert />}
        </PreviewHeaderContainer>
      </PreviewHeader>
      <PreviewFilters>
        <DesignSystemThemeProvider>
          <AdvancedDatePicker
            value={selectedDate}
            minDate={minDate}
            maxDate={maxDate}
            disabled={!previewData?.activeAssembly}
            width={245}
            onChange={(date) => handleChangeSelectedDate(date)}
          />
        </DesignSystemThemeProvider>

        <StyledStack direction="row">
          <RoundedDropdown
            id="screenSelector"
            onChange={(e: SelectChangeEvent<string>) =>
              handleChangeSelectedScreen(e.target.value)
            }
            value={selectedScreen}
            disabled={!previewData?.activeAssembly}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 1,
                },
              },
            }}>
            {isSelectedScreenInvalid && (
              <MenuItem value={selectedScreen} disabled>
                {`Screen ${selectedScreen}`}
              </MenuItem>
            )}

            <MenuItem value={DEFAULT_SCREEN_ALL}>
              {lang.scheduleComponent.screenDropdownAll}
            </MenuItem>
            {[...Array(totalScreens)].map((_, index) => {
              const value = index + 1;

              return (
                <MenuItem key={value} value={value}>
                  {`Screen ${value}`}
                </MenuItem>
              );
            })}
          </RoundedDropdown>

          {uniqueSortedPositions && (
            <RoundedDropdown
              id="positionDropdown"
              value={selectedPosition}
              onChange={(e: SelectChangeEvent<string>) =>
                handleChangeSelectedPosition(e.target.value)
              }
              disabled={!previewData?.activeAssembly}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 1,
                  },
                },
              }}>
              {isSelectedPositionInvalid && (
                <MenuItem value={selectedPosition} disabled>
                  {`Position ${selectedPosition}`}
                </MenuItem>
              )}

              {uniqueSortedPositions.map((position) => (
                <MenuItem key={position} value={position}>
                  {`Position ${position}`}
                </MenuItem>
              ))}
            </RoundedDropdown>
          )}

          <RoundedDropdown
            id="daypartSelector"
            value={daypartOptions ? selectedDaypart : ''}
            onChange={(e: SelectChangeEvent<string>) =>
              handleChangeSelectedDaypart(e.target.value)
            }
            disabled={!!changeSetId}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 1,
                },
              },
            }}>
            {!isLoadingDayparts &&
              daypartOptions &&
              [...daypartOptions]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((daypart) => (
                  <MenuItem key={daypart.id} value={daypart.id}>
                    {daypart.name}
                  </MenuItem>
                ))}
          </RoundedDropdown>
        </StyledStack>
      </PreviewFilters>
      <PreviewContentWrapper>
        <ScaleContainer
          enableScaleControls
          scaleBarStyles={{
            position: 'absolute',
            left: SIDE_NAV_WIDTH,
            right: 0,
            bottom: 0,
          }}>
          <PreviewContentBox>
            {changeSetId && (
              <ButtonGroupWrapper>
                <StyledToggleButton
                  size="large"
                  $active={previewOption !== IPreviewOption.AFTER}
                  onClick={() => setPreviewOption(IPreviewOption.BEFORE)}>
                  {lang.scheduleComponent.preview.beforeScheduleButton}
                </StyledToggleButton>
                <StyledToggleButton
                  size="large"
                  $active={previewOption === IPreviewOption.AFTER}
                  onClick={() => setPreviewOption(IPreviewOption.AFTER)}>
                  {lang.scheduleComponent.preview.afterScheduleButton}
                </StyledToggleButton>
              </ButtonGroupWrapper>
            )}

            <LoadingProvider
              isLoading={
                isSharedUtilsLoading || isSchedulePreviewContextLoading
              }>
              {!error ? (
                <GCFPreviewBox>
                  <DesignSystemThemeProvider>
                    {hasChangeSetPreview ? (
                      <>
                        <PreviewLayer $visible={isBeforePreviewVisible}>
                          {renderCachedPreviewLayers(
                            'before',
                            cachedBeforePreviews,
                            isBeforePreviewVisible,
                          )}
                        </PreviewLayer>
                        <PreviewLayer $visible={isAfterPreviewVisible}>
                          {renderCachedPreviewLayers(
                            'after',
                            cachedAfterPreviews,
                            isAfterPreviewVisible,
                          )}
                        </PreviewLayer>
                      </>
                    ) : (
                      renderCachedPreviewLayers(
                        'before',
                        cachedBeforePreviews,
                        true,
                      )
                    )}
                    {!hasActivePreviewAssembly && (
                      <PreviewLayer $visible={true}>
                        <EmptyProvider
                          message={
                            lang.scheduleComponent.preview.noPreviewAvailable
                          }
                          dataToCheck={[]}>
                          <></>
                        </EmptyProvider>
                      </PreviewLayer>
                    )}
                  </DesignSystemThemeProvider>
                </GCFPreviewBox>
              ) : (
                <></>
              )}
            </LoadingProvider>
          </PreviewContentBox>
        </ScaleContainer>
      </PreviewContentWrapper>
    </PreviewWrapper>
  );
};
